import { useState } from "react";
import { Store, Ticket, Users, Sparkles } from "lucide-react";
import VendorRegistrationForm from "@/components/popup/VendorRegistrationForm";
import EarlyBirdOrderForm from "@/components/popup/EarlyBirdOrderForm";

type Mode = null | "vendor" | "early_bird";

const PopUpPage = () => {
  const [mode, setMode] = useState<Mode>(null);

  return (
    <div className="pt-24 pb-16 px-4 min-h-screen bg-background">
      <div className="container mx-auto max-w-4xl">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/15 text-secondary-foreground px-3 py-1 text-xs font-body font-semibold uppercase tracking-wide mb-4">
            <Sparkles className="w-3.5 h-3.5" />
            Community Pop-Up Event
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-3">
            Silver Sage Consignment Pop-Up
          </h1>
          <p className="font-body text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Join our homeschool community for a friendly one-day pop-up. Bring gently-used
            books, curriculum, games, and activities to sell — or come early to shop the best
            finds before doors open to the public.
          </p>
        </div>

        {/* Two-way choice cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
          <button
            onClick={() => {
              setMode("vendor");
              setTimeout(
                () => document.getElementById("action-section")?.scrollIntoView({ behavior: "smooth" }),
                50,
              );
            }}
            className={`text-left rounded-xl border-2 p-6 transition-all ${
              mode === "vendor"
                ? "border-primary bg-primary/5 shadow-md"
                : "border-border bg-card hover:border-primary/40 hover:shadow-sm"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-primary/10 p-3 shrink-0">
                <Store className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground mb-1">
                  Register as a Vendor
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Sell your own gently-used items. Rent a table from us or bring your own.
                </p>
                <p className="text-sm font-semibold text-primary mt-2">$5 or $15 →</p>
              </div>
            </div>
          </button>

          <button
            onClick={() => {
              setMode("early_bird");
              setTimeout(
                () => document.getElementById("action-section")?.scrollIntoView({ behavior: "smooth" }),
                50,
              );
            }}
            className={`text-left rounded-xl border-2 p-6 transition-all ${
              mode === "early_bird"
                ? "border-primary bg-primary/5 shadow-md"
                : "border-border bg-card hover:border-primary/40 hover:shadow-sm"
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="rounded-full bg-primary/10 p-3 shrink-0">
                <Ticket className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h2 className="font-heading text-xl font-bold text-foreground mb-1">
                  Buy an Early Bird Ticket
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Get in one hour before general admission and shop first.
                </p>
                <p className="text-sm font-semibold text-primary mt-2">$5 per ticket →</p>
              </div>
            </div>
          </button>
        </div>

        <div className="rounded-md bg-secondary/10 border border-secondary/30 p-4 text-sm text-foreground mb-6 flex gap-3">
          <Users className="w-5 h-5 text-secondary-foreground shrink-0 mt-0.5" />
          <p>
            General admission opens one hour after Early Bird entry and is <strong>free</strong>.
            You do not need a ticket for general admission.
          </p>
        </div>

        <div className="rounded-md bg-primary/10 border border-primary/30 p-4 text-sm text-foreground mb-10 flex gap-3">
          <Sparkles className="w-5 h-5 text-primary shrink-0 mt-0.5" />
          <p>
            All proceeds collected through vendor fees, as well as sales from the Silver Sage
            Homeschool Hub table, will be distributed among charities and non-profits near and
            dear to our hearts.
          </p>
        </div>

        {/* Action section */}
        <div id="action-section" className="scroll-mt-24">
          {mode === "vendor" && (
            <section className="rounded-xl bg-card border border-border p-6 md:p-8 shadow-sm">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center gap-2">
                <Store className="w-6 h-6 text-primary" />
                Vendor Registration
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Choose your table option and complete the form below.
              </p>
              <VendorRegistrationForm />
            </section>
          )}

          {mode === "early_bird" && (
            <section className="rounded-xl bg-card border border-border p-6 md:p-8 shadow-sm">
              <h2 className="font-heading text-2xl font-bold text-foreground mb-2 flex items-center gap-2">
                <Ticket className="w-6 h-6 text-primary" />
                Early Bird Ticket
              </h2>
              <p className="text-sm text-muted-foreground mb-6">
                Reserve your spot to shop one hour before general admission.
              </p>
              <EarlyBirdOrderForm />
            </section>
          )}

          {!mode && (
            <div className="text-center text-muted-foreground text-sm py-6">
              Choose an option above to continue.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PopUpPage;
